/**
 * Messaging Security Utilities
 * Provides security validation and sanitization for the messaging system
 */

/**
 * Validates if a user is authorized to access a conversation
 * @param {string} userId - The current user's ID
 * @param {Object} conversation - The conversation object
 * @returns {boolean} - Whether the user is authorized
 */
export const validateConversationAccess = (userId, conversation) => {
  if (!userId || !conversation) {
    return false;
  }

  // Check if user is one of the participants
  const isParticipant = conversation.participant_1 === userId || 
                       conversation.participant_2 === userId ||
                       conversation.recipient_id === userId;

  if (!isParticipant) {
    return false;
  }

  return true;
};

/**
 * Sanitizes message content to prevent XSS and other security issues
 * @param {string} content - The message content to sanitize
 * @returns {string} - Sanitized content
 */
export const sanitizeMessageContent = (content) => {
  if (!content || typeof content !== 'string') {
    return '';
  }

  // Basic HTML sanitization - remove script tags and event handlers
  let sanitized = content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '')
    .replace(/on\w+='[^']*'/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/vbscript:/gi, '')
    .replace(/data:text\/html/gi, '');

  // Limit length to prevent abuse
  const maxLength = 5000;
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength) + '...';
  }

  return sanitized.trim();
};

/**
 * Validates message data before sending
 * @param {Object} messageData - The message data to validate
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateMessageData = (messageData) => {
  const errors = [];

  if (!messageData) {
    errors.push('Message data is required');
    return { isValid: false, errors };
  }

  // Required fields validation
  if (!messageData.conversation_id) {
    errors.push('Conversation ID is required');
  }

  if (!messageData.sender_id) {
    errors.push('Sender ID is required');
  }

  if (!messageData.content || !messageData.content.trim()) {
    errors.push('Message content is required');
  }

  // Content validation
  if (messageData.content && messageData.content.length > 5000) {
    errors.push('Message content exceeds maximum length (5000 characters)');
  }

  // Message type validation
  const validMessageTypes = ['text', 'file', 'interview_request', 'system'];
  if (messageData.message_type && !validMessageTypes.includes(messageData.message_type)) {
    errors.push('Invalid message type');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates user permissions for messaging operations
 * @param {Object} user - The current user object
 * @param {string} operation - The operation being performed ('send', 'read', 'create_conversation')
 * @returns {boolean} - Whether the user has permission
 */
export const validateUserPermissions = (user, operation) => {
  if (!user || !user.id) {
    return false;
  }

  // Check if user account is active
  if (user.status && user.status !== 'active') {
    return false;
  }

  // All authenticated users can perform basic messaging operations
  const allowedOperations = ['send', 'read', 'create_conversation', 'mark_read'];
  
  if (!allowedOperations.includes(operation)) {
    return false;
  }

  return true;
};

/**
 * Validates recipient for new conversations
 * @param {string} senderId - The sender's user ID
 * @param {string} recipientId - The recipient's user ID
 * @returns {boolean} - Whether the recipient is valid
 */
export const validateRecipient = (senderId, recipientId) => {
  if (!senderId || !recipientId) {
    return false;
  }

  if (senderId === recipientId) {
    return false;
  }

  // Additional validation can be added here (e.g., blocking, reporting checks)
  
  return true;
};

/**
 * Validates if a user can send a message to another user
 * @param {string} senderId - The sender's user ID
 * @param {string} recipientId - The recipient's user ID
 * @returns {Promise<{allowed: boolean, reason?: string}>} - Permission result
 */
export const canSendMessage = async (senderId, recipientId) => {
  try {
    // Basic validation
    if (!senderId || !recipientId) {
      return { allowed: false, reason: 'Invalid sender or recipient ID' };
    }

    if (senderId === recipientId) {
      return { allowed: false, reason: 'Cannot send message to yourself' };
    }

    // Check rate limits
    const withinRateLimit = checkRateLimit(senderId);
    if (!withinRateLimit) {
      return { allowed: false, reason: 'Rate limit exceeded. Please try again later.' };
    }

    // Additional security checks can be added here:
    // - Check if users exist
    // - Check if recipient has blocked sender
    // - Check if sender is suspended/banned
    // - Check if conversation is allowed based on user roles

    return { allowed: true };
  } catch (error) {
    console.error('Error checking message permissions:', error);
    return { allowed: false, reason: 'Failed to validate message permissions' };
  }
};

/**
 * Rate limiting check for message sending
 * @param {string} userId - The user ID
 * @returns {boolean} - Whether the user is within rate limits
 */
export const checkRateLimit = (userId) => {
  if (!userId) return false;

  const now = Date.now();
  const rateLimitKey = `message_rate_${userId}`;
  
  // Get stored rate limit data from localStorage (in production, use Redis or similar)
  const stored = localStorage.getItem(rateLimitKey);
  let rateLimitData = stored ? JSON.parse(stored) : { count: 0, resetTime: now + 60000 }; // 1 minute window

  // Reset if time window has passed
  if (now > rateLimitData.resetTime) {
    rateLimitData = { count: 0, resetTime: now + 60000 };
  }

  // Check if user has exceeded rate limit (30 messages per minute)
  const maxMessagesPerMinute = 30;
  if (rateLimitData.count >= maxMessagesPerMinute) {
    return false;
  }

  // Increment count and store
  rateLimitData.count++;
  localStorage.setItem(rateLimitKey, JSON.stringify(rateLimitData));

  return true;
};

/**
 * Logs security events for monitoring
 * @param {string} event - The security event type
 * @param {Object} details - Event details
 */
export const logSecurityEvent = (event, details = {}) => {
  const logEntry = {
    timestamp: new Date().toISOString(),
    event,
    details,
    userAgent: navigator.userAgent,
    url: window.location.href
  };

  // In production, send to security monitoring service
  // Removed console logging for production
  
  // Store locally for debugging (remove in production)
  const securityLogs = JSON.parse(localStorage.getItem('security_logs') || '[]');
  securityLogs.push(logEntry);
  
  // Keep only last 100 entries
  if (securityLogs.length > 100) {
    securityLogs.splice(0, securityLogs.length - 100);
  }
  
  localStorage.setItem('security_logs', JSON.stringify(securityLogs));
};

export default {
  validateConversationAccess,
  sanitizeMessageContent,
  validateMessageData,
  validateUserPermissions,
  validateRecipient,
  canSendMessage,
  checkRateLimit,
  logSecurityEvent
};
